/**
 * mediaUpload.js
 * iOS 앱의 MediaUploadService와 동일한 자체 서버 업로드 방식
 * 서버: http://122.45.112.83:5000
 */
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { MediaServer } from './constants';

const SERVER_URL = MediaServer.baseURL; // https://media.ionice.hockey (웹 API 호출용)
const IOS_SERVER_URL = 'http://122.45.112.83:5000'; // iOS 앱 호환 URL (Firestore 저장용)

// ─── 사진 업로드 (자체 서버) ───────────────────────────────────────────────────
async function uploadPhotoToServer(file, teamId, onProgress) {
  const formData = new FormData();
  formData.append('teamId', teamId);
  formData.append('file', file, file.name);

  const xhr = new XMLHttpRequest();
  return new Promise((resolve, reject) => {
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (e) { reject(new Error('서버 응답 파싱 실패')); }
      } else {
        reject(new Error(`서버 오류 (${xhr.status}): ${xhr.responseText}`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('네트워크 오류')));
    xhr.addEventListener('abort', () => reject(new Error('업로드 취소됨')));
    xhr.open('POST', `${SERVER_URL}/api/media/upload/photo`);

    // Firebase Auth 토큰 (선택적)
    const user = auth.currentUser;
    if (user) {
      user.getIdToken().then(token => {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      }).catch(() => xhr.send(formData));
    } else {
      xhr.send(formData);
    }
  });
}

// ─── 동영상 단순 업로드 (자체 서버, 100MB 이하) ──────────────────────────────
async function uploadVideoToServer(file, teamId, onProgress) {
  const formData = new FormData();
  formData.append('teamId', teamId);
  formData.append('file', file, file.name);

  const xhr = new XMLHttpRequest();
  return new Promise((resolve, reject) => {
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (e) { reject(new Error('서버 응답 파싱 실패')); }
      } else {
        reject(new Error(`서버 오류 (${xhr.status}): ${xhr.responseText}`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('네트워크 오류')));
    xhr.addEventListener('abort', () => reject(new Error('업로드 취소됨')));
    xhr.open('POST', `${SERVER_URL}/api/media/upload/video`);
    xhr.timeout = 600000; // 10분

    const user = auth.currentUser;
    if (user) {
      user.getIdToken().then(token => {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      }).catch(() => xhr.send(formData));
    } else {
      xhr.send(formData);
    }
  });
}

// ─── 동영상 청크 업로드 (100MB 초과) ─────────────────────────────────────────
async function uploadVideoChunked(file, teamId, onProgress) {
  const chunkSize = 5 * 1024 * 1024; // 5MB
  const totalChunks = Math.ceil(file.size / chunkSize);

  const token = auth.currentUser ? await auth.currentUser.getIdToken().catch(() => null) : null;
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  // 1. 초기화
  const initRes = await fetch(`${SERVER_URL}/api/media/upload/video/init`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, teamId, totalChunks }),
  });
  const { fileId } = await initRes.json();

  // 2. 청크 전송
  for (let i = 0; i < totalChunks; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);

    const chunkRes = await fetch(`${SERVER_URL}/api/media/upload/video/chunk`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/octet-stream',
        'X-File-Id': fileId,
        'X-Chunk-Index': String(i),
        'X-Total-Chunks': String(totalChunks),
      },
      body: chunk,
    });
    if (!chunkRes.ok) throw new Error(`청크 ${i} 업로드 실패`);
    if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 100));
  }

  // 3. 완료
  const completeRes = await fetch(`${SERVER_URL}/api/media/upload/video/complete`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileId, fileName: file.name, teamId }),
  });
  return completeRes.json();
}

// ─── 동영상 썸네일 생성 (첫 프레임 캡처) ──────────────────────────────────────
function generateVideoThumbnail(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.style.display = 'none';
    document.body.appendChild(video);

    const objectUrl = URL.createObjectURL(file);
    let resolved = false;

    const cleanup = () => {
      if (!resolved) {
        resolved = true;
        URL.revokeObjectURL(objectUrl);
        video.src = '';
        video.load();
        video.remove();
      }
    };

    const captureFrame = () => {
      if (resolved) return;
      try {
        const canvas = document.createElement('canvas');
        const maxDim = 640;
        let w = video.videoWidth || 320;
        let h = video.videoHeight || 240;
        if (w > maxDim || h > maxDim) {
          const ratio = Math.min(maxDim / w, maxDim / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);

        canvas.toBlob((blob) => {
          console.log('✅ 썸네일 생성 성공:', blob?.size, 'bytes');
          cleanup();
          resolve(blob);
        }, 'image/jpeg', 0.7);
      } catch (err) {
        console.error('❌ 썸네일 캡처 실패:', err);
        cleanup();
        resolve(null);
      }
    };

    video.addEventListener('seeked', captureFrame, { once: true });

    video.addEventListener('loadedmetadata', () => {
      if (resolved) return;
      console.log('📹 메타데이터 로드됨, duration:', video.duration);
      video.currentTime = Math.min(1, (video.duration || 2) * 0.3);
    }, { once: true });

    video.addEventListener('error', () => {
      console.error('❌ 비디오 로드 오류');
      cleanup();
      resolve(null);
    }, { once: true });

    video.src = objectUrl;

    setTimeout(() => {
      if (!resolved) {
        console.warn('⏰ 썸네일 생성 타임아웃');
        cleanup();
        resolve(null);
      }
    }, 8000);
  });
}

// ─── 썸네일을 서버에 사진으로 업로드 ─────────────────────────────────────────
async function uploadThumbnailToServer(blob, teamId) {
  const fileName = `thumb_${Date.now()}.jpg`;
  const file = new File([blob], fileName, { type: 'image/jpeg' });

  try {
    const response = await uploadPhotoToServer(file, teamId, null);
    return `${IOS_SERVER_URL}${response.url}`;
  } catch (err) {
    console.error('썸네일 업로드 실패:', err);
    return null;
  }
}

// ─── 동영상 duration 추출 ────────────────────────────────────────────────────
function getVideoDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
    video.addEventListener('loadedmetadata', () => {
      const duration = video.duration;
      URL.revokeObjectURL(objectUrl);
      video.remove();
      resolve(isFinite(duration) ? duration : null);
    });
    video.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      video.remove();
      resolve(null);
    });
    setTimeout(() => resolve(null), 3000);
  });
}

// ─── 통합 업로드 함수 ────────────────────────────────────────────────────────
/**
 * 미디어 파일을 자체 서버에 업로드하고 Firestore에 기록
 * iOS MediaUploadService와 동일한 스키마
 *
 * @param {File} file - 업로드할 파일
 * @param {string} teamId - 팀 ID
 * @param {object} options - { context, contextId, uploaderName, onProgress }
 * @returns {object} Firestore에 저장된 미디어 문서 데이터
 */
export async function uploadMediaFile(file, teamId, options = {}) {
  const { context = 'general', contextId = null, uploaderName = '', onProgress } = options;
  const isVideo = file.type.startsWith('video/');
  const isPhoto = file.type.startsWith('image/');

  if (!isVideo && !isPhoto) throw new Error('지원하지 않는 파일 형식');

  let response;
  let thumbnailUrl = null;
  let duration = null;

  if (isPhoto) {
    response = await uploadPhotoToServer(file, teamId, onProgress);
  } else {
    // 동영상: 썸네일 생성 + duration 추출 (업로드와 병행)
    const [thumbBlob, videoDuration] = await Promise.all([
      generateVideoThumbnail(file),
      getVideoDuration(file),
    ]);
    duration = videoDuration;

    if (file.size <= 100 * 1024 * 1024) {
      response = await uploadVideoToServer(file, teamId, onProgress);
    } else {
      response = await uploadVideoChunked(file, teamId, onProgress);
    }

    // 썸네일 업로드 (영상 업로드 완료 후)
    if (thumbBlob) {
      thumbnailUrl = await uploadThumbnailToServer(thumbBlob, teamId);
    }
  }

  // Firestore 문서 데이터 (iOS MediaItem과 동일 스키마)
  const mediaData = {
    teamId: teamId,
    uploadedBy: auth.currentUser?.uid || 'unknown',
    uploaderName: uploaderName || auth.currentUser?.displayName || auth.currentUser?.email || '',
    type: isVideo ? 'video' : 'photo',
    url: `${IOS_SERVER_URL}${response.url}`,
    thumbnailUrl: thumbnailUrl || (response.thumbnailUrl ? `${IOS_SERVER_URL}${response.thumbnailUrl}` : null),
    fileName: file.name,
    fileSize: response.fileSize || file.size,
    mimeType: response.mimeType || file.type,
    width: response.width || null,
    height: response.height || null,
    duration: duration || response.duration || null,
    context: context,
    contextId: contextId,      // iOS와 동일: scheduleId를 contextId에 저장
    caption: '',
    tags: [],
    visibility: 'team',
    likeCount: 0,
    likedBy: [],
    commentCount: 0,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  // Firestore에 저장
  const docRef = await addDoc(collection(db, 'teams', teamId, 'media'), mediaData);
  return { id: docRef.id, ...mediaData };
}

// ─── 이미지만 업로드 (Firestore 미디어 문서 생성 없음) ────────────────────────
/**
 * 드릴 도해처럼 팀 미디어 갤러리에 노출되면 안 되는 이미지용.
 * 서버에 파일만 올리고 URL을 돌려준다. 저장은 호출부가 알아서 한다.
 * URL은 https://media.ionice.hockey 정규 도메인으로 반환 (iOS ATS가 http 차단).
 */
export async function uploadImageOnly(file, teamId, onProgress) {
  if (!file.type.startsWith('image/')) throw new Error('이미지 파일만 업로드할 수 있습니다');
  const response = await uploadPhotoToServer(file, teamId, onProgress);
  return {
    url: `${SERVER_URL}${response.url}`,
    fileName: file.name,
    fileSize: response.fileSize || file.size,
    width: response.width || null,
    height: response.height || null,
  };
}

// ─── 서버 상태 확인 ──────────────────────────────────────────────────────────
export async function checkMediaServerHealth() {
  try {
    const res = await fetch(`${SERVER_URL}/api/media/health`, { timeout: 5000 });
    return res.ok;
  } catch {
    return false;
  }
}
